/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.dashboard.list",
  title: "Dashboard List View",
  description: "A reusable dashboard list view shell with search, filters, view toggle, results area, and pagination. Populated client-side via DashboardAPI.",
  styling: {
    styles: [
      {
        selector: ".ua-dashboard-list-toolbar",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-bottom: 1rem;"
        ]
      },
      {
        selector: ".ua-dashboard-list-toolbar .ua-dashboard-search",
        styles: [
          "flex: 1 1 14rem;",
          "min-width: 10rem;"
        ]
      },
      {
        selector: ".ua-dashboard-list-results",
        styles: [
          "min-height: 4rem;"
        ]
      },
      {
        selector: ".ua-dashboard-list-skeleton",
        styles: [
          "display: flex;",
          "flex-direction: column;",
          "gap: .5rem;"
        ]
      },
      {
        selector: ".ua-dashboard-skeleton-row",
        styles: [
          "height: 2.5rem;",
          "border-radius: .5rem;",
          "background: linear-gradient(90deg, var(--bs-secondary-bg) 25%, var(--bs-tertiary-bg) 50%, var(--bs-secondary-bg) 75%);",
          "background-size: 200% 100%;",
          "animation: ua-skeleton-shimmer 1.4s infinite;"
        ]
      },
      {
        selector: "@keyframes ua-skeleton-shimmer",
        styles: [
          "0% { background-position: 200% 0; }",
          "100% { background-position: -200% 0; }"
        ]
      },
      {
        selector: ".ua-dashboard-list-pagination",
        styles: [
          "display: flex;",
          "align-items: center;",
          "justify-content: space-between;",
          "flex-wrap: wrap;",
          "gap: .5rem;",
          "margin-top: 1rem;",
          "padding-top: .75rem;",
          "padding-bottom: .75rem"
          // "border-top: 1px solid var(--bs-border-color);"
        ]
      },
      {
        selector: ".ua-dashboard-list-view-toggle .btn",
        styles: [
          "padding: .25rem .5rem;"
        ]
      }
    ]
  },
  fields: {
    "page-namespace": {
      title: "Page Namespace",
      type: "string",
      description: "The dashboard page namespace used to build API calls and navigation links.",
      required: true
    },
    "api-url": {
      title: "API URL",
      type: "string",
      description: "Full API base URL for this page (e.g. /api/my/app.site-content). Overrides the default /api/<namespace> construction. Set from context.page.routes.api in content functions."
    },
    "page-url": {
      title: "Page URL",
      type: "string",
      description: "Base page URL for building edit/detail links (e.g. /dashboard/my/app-site-content). Overrides the default /dashboard/<slug> construction. Set from context.page.routes.page in content functions."
    },
    "searchable": {
      title: "Searchable",
      type: "boolean",
      description: "Show a search input in the toolbar.",
      default: true
    },
    "can-create": {
      title: "Can Create",
      type: "boolean",
      description: "Show a create/new button in the toolbar.",
      default: false
    },
    "create-label": {
      title: "Create Label",
      type: "string",
      description: "Label for the create button.",
      default: "New"
    },
    "create-href": {
      title: "Create Href",
      type: "string",
      description: "URL for the create button. Defaults to /dashboard/<slug>/new."
    },
    "view-mode": {
      title: "View Mode",
      type: "string",
      description: "Display mode: list (line items) or card (gallery/preview grid).",
      list: ["list", "card"],
      default: "list"
    },
    "allow-view-toggle": {
      title: "Allow View Toggle",
      type: "boolean",
      description: "Show list/card view toggle buttons in the toolbar.",
      default: false
    },
    "empty-message": {
      title: "Empty Message",
      type: "string",
      description: "Message shown when no results are found.",
      default: "No items found."
    },
    "filters": {
      title: "Filters",
      type: "array",
      description: "Optional filter definitions. Each filter: { name, label, options: [{value, label}] }."
    },
    "page-size": {
      title: "Page Size",
      type: "noattr.number",
      description: "Number of items per page.",
      default: 25
    }
  }
});

var uai = require("../interface/interface.js");

/**
 * Render the dashboard list view shell.
 * The results area is populated client-side via DashboardAPI and pageinit handlers.
 */
exports.render = function(options) {
  var pageNamespace = (options["page-namespace"] || "").toString().trim();
  var apiUrl        = (options["api-url"]  || "").toString().trim();
  var pageUrl       = (options["page-url"] || "").toString().trim();
  var searchable    = options.searchable !== false;
  var canCreate     = options["can-create"] === true;
  var createLabel   = (options["create-label"] || "New").toString();
  var createHref    = (options["create-href"] || "").toString().trim();
  var viewMode      = (options["view-mode"] || "list").toString().toLowerCase();
  var allowToggle   = options["allow-view-toggle"] === true;
  var emptyMessage  = (options["empty-message"] || "No items found.").toString();
  var filters       = options.filters instanceof Array ? options.filters : [];
  var pageSize      = typeof options["page-size"] === "number" ? options["page-size"] : 25;

  // Build create href: prefer explicit, then page-url + /new, then slug fallback
  if (!createHref) {
    var slug = pageNamespace.replace(/\./g, "-");
    createHref = pageUrl ? (pageUrl.replace(/\/$/, "") + "/new") : ("/dashboard/" + slug + "/new");
  }

  var uid = uai.generateRandomID(8);
  var searchId = "ua-dash-search-" + uid;
  var resultsId = "ua-dash-results-" + uid;
  var paginationId = "ua-dash-pagination-" + uid;
  var countId = "ua-dash-count-" + uid;

  // --- Toolbar ---
  var toolbarItems = [];

  if (searchable) {
    toolbarItems.push({
      n: "input",
      id: searchId,
      c: "form-control ua-dashboard-search",
      type: "search",
      placeholder: "Search...",
      "autocomplete": "off",
      "data-dashboard-search": "true"
    });
  }

  // Filter dropdowns
  for (var f = 0; f < filters.length; f++) {
    var filter = filters[f];
    var filterOptions = (filter.options instanceof Array ? filter.options : []).map(function(opt) {
      return { n: "option", value: (opt.value || ""), i: (opt.label || opt.value || "") };
    });
    filterOptions.unshift({ n: "option", value: "", i: (filter.label || filter.name || "Filter") });
    toolbarItems.push({
      n: "select",
      c: "form-select form-select-sm",
      style: "min-width: 6rem; max-width: 11rem; flex: 0 1 auto;",
      "data-dashboard-filter": filter.name || ("filter-" + f),
      i: filterOptions
    });
  }

  // View toggle
  if (allowToggle) {
    toolbarItems.push({
      n: "div",
      c: "btn-group ua-dashboard-list-view-toggle ms-auto",
      role: "group",
      i: [
        {
          n: "button",
          type: "button",
          c: "btn btn-outline-secondary" + (viewMode === "list" ? " active" : ""),
          title: "List view",
          "data-dashboard-viewmode": "list",
          i: { n: "i", c: "bi bi-list-task" }
        },
        {
          n: "button",
          type: "button",
          c: "btn btn-outline-secondary" + (viewMode === "card" ? " active" : ""),
          title: "Card view",
          "data-dashboard-viewmode": "card",
          i: { n: "i", c: "bi bi-grid-3x3-gap" }
        }
      ]
    });
  }

  // Create button (pushed to right if no toggle)
  if (canCreate) {
    toolbarItems.push({
      n: "a",
      href: createHref,
      c: "btn btn-primary" + (allowToggle ? "" : " ms-auto"),
      i: [
        { n: "i", c: "bi bi-plus-lg me-1" },
        createLabel
      ]
    });
  }

  // --- Skeleton loading rows ---
  var skeletonRows = [];
  for (var s = 0; s < 4; s++) {
    skeletonRows.push({ n: "div", c: "ua-dashboard-skeleton-row" });
  }

  // --- Full structure ---
  return {
    n: "div",
    "data-ua-dashboard-list": "true",
    "data-dashboard-page-namespace": pageNamespace,
    "data-dashboard-api-url":  apiUrl  || undefined,
    "data-dashboard-page-url": pageUrl || undefined,
    "data-dashboard-view-mode": viewMode,
    "data-dashboard-page-size": pageSize.toString(),
    "data-dashboard-empty-message": emptyMessage,
    i: [
      // Toolbar
      {
        n: "div",
        c: "ua-dashboard-list-toolbar",
        i: toolbarItems
      },

      // Result count summary
      {
        n: "div",
        id: countId,
        c: "text-muted small mb-2",
        "data-dashboard-count": "true",
        i: ""
      },

      // Results area (skeleton while loading)
      {
        n: "div",
        id: resultsId,
        c: "ua-dashboard-list-results",
        "data-dashboard-results": "true",
        i: {
          n: "div",
          c: "ua-dashboard-list-skeleton",
          i: skeletonRows
        }
      },

      // Pagination
      {
        n: "div",
        id: paginationId,
        c: "ua-dashboard-list-pagination",
        "data-dashboard-pagination": "true",
        i: ""
      }
    ]
  };
};
